import React, { Ref, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { StanButton } from '../StanButton/StanButton';
import { StanFileDropZonePreview } from './MultiRowPreview/StanMultiRowFileDropZonePreview';
import { StanSingleFileDropZonePreview } from './SinglePreview/StanSingleFileDropZonePreview';
import { StanFileUpload, StanFileUploadRef } from '../StanFileUpload/StanFileUpload';
import { StanFileDropZoneProps, StanFileDropZoneRef } from '../types';
import { FileState } from '@/hooks/useFileUpload';
import styles from './StanFileDropZone.module.css';
import IconUpload from '@/assets/icons/ic-file-upload.svg';
import { BareSvg, cn } from '@/lib';

// TODO - Better error handling

interface StanFileDropZonePropsWithRef extends StanFileDropZoneProps {
    ref?: Ref<StanFileDropZoneRef>;
}

export const StanFileDropZone = ({
    multiple = false,
    accept = '*',
    dropzonePlaceholder = multiple ? 'Drag Your Files Here' : 'Drag Your File Here',
    placeholder = multiple ? 'Drag Your Files Here' : 'Drag Your File Here',
    buttonText = multiple ? 'Upload Files' : 'Upload File',
    onFilesChanged,
    onUploaded,
    onProgress,
    onError,
    isUploadConfirmed = true,
    className = '',
    ghost = false,
    objectType = 'unknown',
    ref,
}: StanFileDropZonePropsWithRef) => {
    const fileUploadRef = useRef<StanFileUploadRef>(null);
    const [fileStates, setFileStates] = useState<FileState[]>([]);

    const disableFileUpload = useMemo(() => {
        return !multiple && fileStates.length > 0;
    }, [multiple, fileStates]);

    const isUploadingSingleFile = useMemo(() => {
        return (
            !multiple &&
            fileStates.some(f => f.status === 'uploading') &&
            fileStates.some(currentFile => currentFile.status === 'uploading')
        );
    }, [multiple, fileStates]);

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            if (acceptedFiles && acceptedFiles.length > 0) {
                fileUploadRef.current?.addfiles?.(acceptedFiles);
            }
        },
        [fileUploadRef]
    );

    const {
        getRootProps,
        getInputProps: _getInputProps,
        isDragActive,
        open: _open,
    } = useDropzone({
        onDrop,
        accept: accept !== '*' ? { [accept]: [] } : undefined,
        multiple,
        noClick: true, // We'll handle clicks manually
        noKeyboard: true,
        disabled: disableFileUpload,
    });

    useImperativeHandle(ref, () => ({
        openFilePicker: () => fileUploadRef.current?.onPickFile(),
        upload: () => fileUploadRef.current?.upload(),
        clearFile: () => fileUploadRef.current?.clearFile(),
    }));

    const handleDeleteFile = useCallback(
        (idx: number) => {
            // This will be handled by the StanFileUpload component
            fileUploadRef.current?.deleteFile?.(idx);
        },
        [fileUploadRef]
    );

    useEffect(() => {
        onFilesChanged?.(fileStates);
    }, [fileStates, onFilesChanged]);

    const handleFileStatesChange = useCallback((newFileStates: FileState[]) => {
        setFileStates(newFileStates);
    }, []);

    const singleFileState: FileState | undefined = useMemo(() => {
        return fileStates.length > 0 ? fileStates[0] : undefined;
    }, [fileStates]);

    const fileUploadComponent = (
        <StanFileUpload
            ref={fileUploadRef}
            accept={accept}
            multiple={multiple}
            confirmUpload={isUploadConfirmed}
            objectType={objectType}
            onUploaded={onUploaded}
            onProgress={onProgress}
            onError={onError}
            onFileStatesChange={handleFileStatesChange}
        />
    );

    if (ghost) {
        return fileUploadComponent;
    }
    return (
        <>
            <div
                {...getRootProps()}
                className={`${className} ${styles.dropZoneWrapper} ${isDragActive ? styles.dragging : ''}`}
                onClick={() => {
                    if ((!multiple && fileStates.length === 0) || multiple) {
                        fileUploadRef.current?.onPickFile();
                    }
                }}
                tabIndex={-1}
            >
                {fileUploadComponent}
                {isDragActive && (
                    <div className='animate-in fade-in absolute inset-0 z-50 flex items-center justify-center'>
                        <div className='text-brand-main flex flex-col items-center gap-4'>
                            <BareSvg SvgComponent={IconUpload} className='size-10' removeFill />
                            <div className='text-body-sm text-brand-main'>{dropzonePlaceholder}</div>
                        </div>
                    </div>
                )}
                <div className={cn(isDragActive ? 'animate-out fade-out fill-mode-forwards' : 'animate-in fade-in')}>
                    <div className={styles.dropZoneContent}>
                        {!multiple && singleFileState && (
                            <StanSingleFileDropZonePreview file={singleFileState} onDelete={handleDeleteFile} />
                        )}

                        <div className={styles.dropZoneText}>
                            <p className={styles.placeholderText}>{placeholder}</p>
                            <StanButton
                                type='button'
                                name='file-upload-select'
                                size='xs'
                                variant='outline'
                                disabled={disableFileUpload}
                                loading={isUploadingSingleFile}
                            >
                                {buttonText}
                            </StanButton>
                        </div>
                    </div>
                </div>
            </div>
            {multiple && <StanFileDropZonePreview fileStates={fileStates} onDelete={handleDeleteFile} />}
        </>
    );
};

StanFileDropZone.displayName = 'StanFileDropZone';
